//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import Radium from "radium";
import { createFragmentContainer } from "react-relay";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
// home-uninstall imports
import { IContainer, IProps, IState } from "./HomeUninstall.d";
import { homeUninstallFragments } from "./HomeUninstall.gql";
import { styles, survey } from "./HomeUninstall.styles";
// other components
import { LoginBtn } from "../login-btn";
import { RedButton } from "@clockwise/web-commons/src/ui/red-button";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setDeactivated, setUninstalled } from "@actions/auth.actions";
import { snackBarOpen } from "@actions/snack-bar.actions";
// util
import { doUserLogout } from "@util/auth.util";
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
import { userDeactivated } from "@clockwise/web-commons/src/util/local-storage";
// import { logger } from "@util/logger.util";
import {
  loginUser,
  recordChromeUninstall,
  deactivateUser,
  saveUninstallResponse,
} from "@mutations";
import { H1, H2, P } from "@ui";
import { windowLocation } from "@util/location.util";
// material-ui
import {
  Paper,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import withStyles from "@material-ui/core/styles/withStyles";
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// COMPONENT
//////////////////
@Radium
class HomeUninstallCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      reason: "",
      more: "",
      name: "",
      considerClockwiseFuture: "",
      submittedFeedback: false,
      dialogOpen: false,
      trackedSurveyResponse: false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Lifecycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    if (!this.props.uninstalled) {
      page(PageEvents.HOME_UNINSTALL);

      recordChromeUninstall(
        this.props.relay.environment,
        {},
        () => this.onRecordChromeUninstall(true),
        () => this.onRecordChromeUninstall(false),
      );

      this.props.setUninstalled(true);
    }
  }

  private onRecordChromeUninstall = (_result: boolean) => {};

  // ~-~-~-~-~-~-~-
  // Deactivate Helpers
  // ~-~-~-~-~-~-~-
  private onClickDeactivate = () => {
    deactivateUser(
      this.props.relay.environment,
      {},
      () => this.onDeactivateUser(true),
      () => this.onDeactivateUser(false),
    );
  };

  private cancel = () => {
    track(TrackingEvents.UNINSTALL.CANCEL);
    windowLocation.assign("HomeUninstallCancel", "/");
  };

  private onDeactivateUser = (result: boolean) => {
    if (result) {
      track(TrackingEvents.UNINSTALL.DEACTIVATE_SUCCESS);
      doUserLogout();
      this.props.setDeactivated(true);
    } else {
      track(TrackingEvents.UNINSTALL.DEACTIVATE_FAIL);
      this.props.snackBarOpen(
        "Sorry, there was a problem deactivating your account.  Please contact support so that we may resolve this.",
      );
    }
  };

  // ~-~-~-~-~-~-~-
  // Auth Helpers
  // ~-~-~-~-~-~-~-
  private onStartLogin = () => {
    track(TrackingEvents.AUTH.BEGIN_LOGIN);
  };

  private onFail = () => {
    track(TrackingEvents.AUTH.LOGIN_FAILED);
  };

  private onLoginSuccess = () => {
    track(TrackingEvents.UNINSTALL.LOGIN);
  };

  // ~-~-~-~-~-~-~-
  // Survey Helpers
  // ~-~-~-~-~-~-~-
  private onReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.trackedSurveyResponse) {
      track(TrackingEvents.UNINSTALL.SET_SURVEY_RESPONSE);
    }

    this.setState({ reason: event.target.value, trackedSurveyResponse: true });
  };

  private onMoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.trackedSurveyResponse) {
      track(TrackingEvents.UNINSTALL.SET_SURVEY_RESPONSE);
    }

    this.setState({ more: event.target.value, trackedSurveyResponse: true });
  };

  private onConsiderClockwiseFutureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.trackedSurveyResponse) {
      track(TrackingEvents.UNINSTALL.SET_SURVEY_RESPONSE);
    }

    this.setState({ considerClockwiseFuture: event.target.value, trackedSurveyResponse: true });
  };

  private maybeSubmitFeedback = () => {
    const { reason, more, name, considerClockwiseFuture } = this.state;

    // don't submit survey if form is empty
    if (!reason && !more && !considerClockwiseFuture) {
      return;
    }

    track(TrackingEvents.UNINSTALL.SUBMIT_SURVEY);
    saveUninstallResponse(
      this.props.relay.environment,
      { reason, more, name, considerClockwiseFuture },
      () => this.onSaveUninstallResponse(true),
      () => this.onSaveUninstallResponse(false),
    );
  };

  private onSaveUninstallResponse = (result: boolean) => {
    if (result) {
      this.props.snackBarOpen("Thanks for submitting your feedback.");
      this.setState({
        reason: "",
        more: "",
        name: "",
        considerClockwiseFuture: "",
        submittedFeedback: true,
      });
    } else {
      this.props.snackBarOpen("Sorry, there was a problem recording your feedback.");
    }
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public renderDeactivated = () => {
    const { classes } = this.props;

    return (
      <div className={classes.headerContainer}>
        <div className={classes.headerContent}>
          <h1 className={this.props.classes.h1}>Your deactivation request is being processed</h1>
          <h2 className={this.props.classes.h2}>
            It may take up to a few hours to process your deactivation request. Please note that
            your coworkers will still be able to invite you to Clockwise meetings unless you ask
            them to turn off Clockwise for those specific calendar events.
          </h2>
        </div>
      </div>
    );
  };

  public renderAuthed = () => {
    const { classes } = this.props;
    const { submittedFeedback } = this.state;

    return (
      <div>
        <div className={classes.headerContainer}>
          <div className={classes.headerContent}>
            <h1 className={this.props.classes.h1}>Deactivate your Clockwise account</h1>
            <h2 className={this.props.classes.h2}>
              We're sorry to see you go. Before you deactivate, please take a minute to share
              feedback and help us improve Clockwise.
            </h2>
          </div>
        </div>
        <div style={survey.container}>
          <Paper elevation={2} style={survey.paper}>
            <div style={survey.section}>
              <H2>
                Which of the following best reflects the main reason for deactivating your Clockwise
                account?
              </H2>
              <div>
                <RadioGroup
                  aria-label="Reason"
                  name="reason"
                  value={this.state.reason}
                  onChange={this.onReasonChange}
                >
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="I couldn't successfully sign up or get started"
                    label="I couldn't successfully sign up or get started"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="I didn't see the value of Clockwise"
                    label="I didn't see the value of Clockwise"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Clockwise is missing features I need"
                    label="Clockwise is missing features I need"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Clockwise makes unwanted changes to my calendar"
                    label="Clockwise makes unwanted changes to my calendar"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Clockwise is disruptive to others on my team"
                    label="Clockwise is disruptive to others on my team"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="I encountered performance issues or errors"
                    label="I encountered performance issues or errors"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="My company's policy does not allow me to use Clockwise"
                    label="My company's policy does not allow me to use Clockwise"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Clockwise is too expensive for my organization"
                    label="Clockwise is too expensive for my organization"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="I am leaving my company"
                    label="I am leaving my company"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Other"
                    label="Other"
                  />
                </RadioGroup>
              </div>
            </div>
            <div style={survey.section}>
              <H2>Tell us more! Your input is valuable.</H2>
              <div>
                <TextField
                  value={this.state.more}
                  onChange={this.onMoreChange}
                  fullWidth
                  multiline
                />
              </div>
            </div>
            <div style={survey.section}>
              <H2>
                If Clockwise were to address your reason for deactivating your account, would you
                consider using Clockwise again in the future?
              </H2>
              <div>
                <RadioGroup
                  aria-label="ConsiderClockwiseFuture"
                  name="considerClockwiseFuture"
                  value={this.state.considerClockwiseFuture}
                  onChange={this.onConsiderClockwiseFutureChange}
                >
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="Yes"
                    label="Yes"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="No"
                    label="No"
                  />
                  <FormControlLabel
                    control={<Radio classes={{ root: classes.radioRoot }} color="default" />}
                    value="I'm not sure"
                    label="I'm not sure"
                  />
                </RadioGroup>
              </div>
            </div>
            {submittedFeedback ? (
              <div style={survey.section}>Thanks for submitting your feedback!</div>
            ) : null}
          </Paper>
        </div>
        <div className={this.props.classes.buttonContainer}>
          <RedButton
            onClick={() => {
              this.setState({ dialogOpen: true });
            }}
            label="Deactivate your account"
          />
          <Button
            variant="contained"
            onClick={() => {
              this.cancel();
            }}
            style={{ marginLeft: 10 }}
            className={this.props.classes.buttonCancel}
          >
            <span>Cancel</span>
          </Button>
        </div>
      </div>
    );
  };

  public renderUnauthed = () => {
    const { classes } = this.props;
    const redirect = "/uninstall";

    return (
      <div>
        <div className={classes.headerContainer}>
          <div className={classes.headerContent}>
            <h1 className={this.props.classes.h1}>Sign in to deactivate your account</h1>
            <h2 className={this.props.classes.h2}>
              If you want to completely remove Clockwise from your calendar, please sign in to
              deactivate your Clockwise account.
            </h2>
          </div>
        </div>
        <div className={classes.loginContainer}>
          <LoginBtn
            loginUserMutation={loginUser}
            label="Sign in to deactivate"
            googleStandard
            style={styles.deactivateButton}
            labelStyle={styles.deactivateButtonLabel}
            redirect={redirect}
            onStart={() => this.onStartLogin()}
            onSuccess={() => {
              this.onLoginSuccess();
            }}
            onFailure={() => this.onFail()}
          />
        </div>
      </div>
    );
  };

  private renderConfirmationDialog = () => {
    return (
      <Dialog open={this.state.dialogOpen}>
        <DialogTitle>
          <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
            Are you sure you want to deactivate your Clockwise account?
          </H1>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <P
            style={{
              fontSize: 15,
              lineHeight: 1.8,
              color: greys.slate,
              marginBottom: 30,
            }}
          >
            This action cannot be undone. It may take up to a few hours to process your deactivation
            request.
          </P>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => this.setState({ dialogOpen: false })}>
            Cancel
          </Button>
          <RedButton
            onClick={() => {
              track(TrackingEvents.UNINSTALL.DEACTIVATE_START);
              this.maybeSubmitFeedback();
              this.onClickDeactivate();
              this.setState({ dialogOpen: false });
            }}
            label="Deactivate"
          />
        </DialogActions>
      </Dialog>
    );
  };

  public render() {
    const authenticated = this.props.viewer.user && this.props.viewer.user.id;
    const description = "Help with uninstalling Clockwise";
    const deactivated = this.props.deactivated || userDeactivated.get();

    return (
      <div className={this.props.classes.container}>
        <Helmet>
          <title>Uninstall | Clockwise</title>
          <meta name="description" content={description} />
          <meta property="og:description" content={description} />
          <meta name="twitter:description" content={description} />
          <meta itemProp="description" content={description} />
        </Helmet>

        {deactivated
          ? this.renderDeactivated()
          : authenticated
          ? this.renderAuthed()
          : this.renderUnauthed()}

        {this.renderConfirmationDialog()}
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
const HomeUninstallStyled = withStyles(styles)(HomeUninstallCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    deactivated: state.auth.deactivated,
    uninstalled: state.auth.uninstalled,
    authed: state.auth.authed,
  }; // add redux state here, if needed
}

const reduxContainer = connect(mapStateToProps, {
  setDeactivated,
  setUninstalled,
  snackBarOpen,
})(HomeUninstallStyled);

//////////////////
// RELAY
//////////////////
export const HomeUninstall = createFragmentContainer<IContainer>(
  reduxContainer,
  homeUninstallFragments,
);
